import {
  supabase, uid,
  buildEntityImageColumns, buildEntityImageFocusColumns, safeDeleteStoredImage,
  getStoredImageAsset, sameStoredImageAsset,
  ENTITY_IMAGE_SELECT,
} from './shared'
import { mapPersonaFromSupabase, mapPersonasFromSupabase } from '../../mappers/personas'
import type { PersonaCreateData, PersonaUpdateData } from '../../contract'

export async function getPersonas() {
  const userId = await uid();
  const { data, error } = await supabase
    .from('personas')
    .select('*')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('id', { ascending: true });

  if (error) throw error;
  return { personas: mapPersonasFromSupabase(data) };
}

export async function getPersona(id: number | string) {
  const userId = await uid();
  const { data, error } = await supabase
    .from('personas')
    .select('*')
    .eq('user_id', userId)
    .eq('id', id)
    .single();

  if (error) throw error;
  return mapPersonaFromSupabase(data);
}

export async function createPersona(personaData: PersonaCreateData) {
  const userId = await uid();
  const { img_asset, img_focus, ...personaFields } = personaData;
  const payload = {
    ...personaFields,
    ...(img_asset !== undefined ? buildEntityImageColumns(img_asset, img_focus) : {}),
    ...(img_asset === undefined && img_focus !== undefined ? buildEntityImageFocusColumns(img_focus) : {}),
    user_id: userId,
  };

  if (personaData?.is_default) {
    const { error: unsetErr } = await supabase
      .from('personas')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);

    if (unsetErr) throw unsetErr;
  }

  const { data, error } = await supabase
    .from('personas')
    .insert(payload)
    .select()
    .single();

  if (error) {
    if (img_asset) {
      await safeDeleteStoredImage(img_asset);
    }
    throw error;
  }

  return {
    message: 'Persona created successfully',
    persona: mapPersonaFromSupabase(data),
  };
}

export async function updatePersona(id: number | string, personaData: PersonaUpdateData = {}) {
  const userId = await uid();
  const { img_asset, img_focus, ...personaFields } = personaData;
  const imageAssetProvided = Object.prototype.hasOwnProperty.call(personaData || {}, 'img_asset');
    const previousImage = imageAssetProvided
      ? getStoredImageAsset(
        (await supabase
          .from('personas')
          .select(ENTITY_IMAGE_SELECT)
          .eq('id', id)
          .eq('user_id', userId)
          .maybeSingle()
        ).data,
        'img',
      )
    : null;

  if (personaData?.is_default) {
    const { error: unsetErr } = await supabase
      .from('personas')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);

    if (unsetErr) throw unsetErr;
  }

  const payload = {
    ...personaFields,
    ...(imageAssetProvided ? buildEntityImageColumns(img_asset, img_focus) : {}),
    ...(!imageAssetProvided && img_focus !== undefined ? buildEntityImageFocusColumns(img_focus) : {}),
  }

  const { error } = await supabase
    .from('personas')
    .update(payload)
    .eq('id', id)
    .eq('user_id', userId);

  if (error) {
    if (img_asset) {
      await safeDeleteStoredImage(img_asset);
    }
    throw error;
  }
  if (imageAssetProvided && previousImage && !sameStoredImageAsset(previousImage, img_asset)) {
    // A playthrough retains the selected portrait as part of its player snapshot.
    // If the reference check fails, keep the asset rather than break a saved story.
    try {
      const { data: references, error: referenceError } = await supabase
        .from('open_world_playthroughs')
        .select('id')
        .contains('persona', { img_bucket: previousImage.bucket, img_storage_path: previousImage.path })
        .limit(1);
      if (!referenceError && references?.length === 0) await safeDeleteStoredImage(previousImage);
    } catch { /* Keep the portrait when its saved references cannot be checked. */ }
  }
  return { message: 'Persona updated successfully' };
}

export async function deletePersona(id: number | string) {
  const userId = await uid();
  const { error } = await supabase
    .from('personas')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
  return { message: 'Persona deleted successfully' };
}

export async function setDefaultPersona(id: number | string) {
  const userId = await uid();

  const { error: unsetErr } = await supabase
    .from('personas')
    .update({ is_default: false })
    .eq('user_id', userId)
    .eq('is_default', true);

  if (unsetErr) throw unsetErr;

  const { error: setErr } = await supabase
    .from('personas')
    .update({ is_default: true })
    .eq('id', id)
    .eq('user_id', userId);

  if (setErr) throw setErr;

  return { message: 'Persona set as default successfully' };
}
